import React from 'react'
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'

import AddressBlock from '../AddressBlock'
import { getCityWithZip } from 'src/Libs/utils/string-functions'
import { Link as RouterLink } from 'react-router-dom'
import { CompanyWithAccountLinkData } from 'src/Libs/service-dashboard'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { J2Editacontent, J2Multiply, J2Validate } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { useUserAccessAndInvitations } from 'src/Libs/installation-rights-management/hooks'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAccountLinkByAccountId } from 'src/Libs/installation-details/hooks'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { getCompactTableSx, tableCellButtonSx, tableCellIconButtonSx } from '../table-styles'

// #region StartAtComponent
interface StartAtComponentProps {
  value: string | undefined
  company: CompanyTableRow
  isInstallationDecommissioned: boolean
  onEditAccessStartAt: (company: CompanyTableRow) => void
}

const StartAtComponent: React.FC<StartAtComponentProps> = ({
  value,
  company,
  isInstallationDecommissioned,
  onEditAccessStartAt,
}): React.ReactElement => {
  const { t } = useCustomTranslation()

  return (
    <Stack direction="row" alignItems="center">
      <Box component="span">{value ? format(new Date(value), t('installation.details.info.owner-start-access-at-format')) : '-'}</Box>
      {isInstallationDecommissioned ? null : (
        <IconButton
          size="small"
          sx={tableCellIconButtonSx}
          onClick={() => onEditAccessStartAt(company)}
          aria-label={t('installation.details.info.edit-access-start-at.button-title')}
        >
          <J2Editacontent />
        </IconButton>
      )}
    </Stack>
  )
}

// #endregion

// #region CompanyAddress
interface CompanyAddressProps {
  company: CompanyTableRow | undefined
}

const CompanyAddress: React.FC<CompanyAddressProps> = ({ company }): React.ReactElement => {
  const { t } = useCustomTranslation()

  return (
    <AddressBlock
      street={company?.locationPlaceOr ?? undefined}
      city={getCityWithZip(company?.locationPlz ?? undefined, company?.locationAddress ?? undefined)}
      country={company?.locationCountry ? t(`common.countries.short.${company.locationCountry}`) : undefined}
    />
  )
}
// #endregion

// #region CompanyName
interface CompanyNameProps {
  value: string
  companyId: number
}

const CompanyName: React.FC<CompanyNameProps> = ({ value, companyId }) => {
  return (
    <Link component={RouterLink} to={`/overview/companies/${companyId}`} sx={{ overflowWrap: 'anywhere' }}>
      {value}
    </Link>
  )
}
// #endregion

// #region RenderAccessIcon
interface RenderAccessIconProps {
  hasAccess: boolean
}

const RenderAccessIcon: React.FC<RenderAccessIconProps> = ({ hasAccess }): React.ReactElement => {
  return <Box sx={{ display: 'flex' }}>{hasAccess ? <J2Validate sx={{ color: 'success.main' }} /> : <J2Multiply sx={{ color: 'error.main' }} />}</Box>
}
// #endregion

// #region CompaniesWithAccess
type CompanyTableRow = CompanyWithAccountLinkData & {
  remove: React.ReactNode
}

interface CompaniesWithAccessProps {
  companies: CompanyWithAccountLinkData[]
  installationId: string | undefined
  isInstallationDecommissioned: boolean
  refetchAttachedCompanies: () => void
}

/**
 * Component to display the details of all companies that have access to an installation
 * @param companies - Array of companies with their corresponding access rights
 * @param installationId - Installation id
 * @param refetchAttachedCompanies - Function to refetch attached companies
 */
const CompaniesWithAccess: React.FC<CompaniesWithAccessProps> = ({
  companies,
  installationId,
  isInstallationDecommissioned,
  refetchAttachedCompanies,
}) => {
  const { t } = useCustomTranslation()
  const { openReasonDialog, openEditAccessStartAtModal } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const getAccountLinkByAccountId = useAccountLinkByAccountId()

  const { removeAccountFromAccess } = useUserAccessAndInvitations({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.remove-access.success-message'))
      refetchAttachedCompanies()
    },
    onError: error => {
      const fetchError = error as AuthFetchError
      if (fetchError?.response?.data.errno === FetchErrorCodes.InstallationAdminCannotBeDeleted) {
        enqueueErrorStack(t('installation.details.info.remove-access.owner-error-message'))
        return
      }

      enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
    },
  })

  const onRemoveUserFromCompany = async (companyId: number, reason: string): Promise<void> => {
    if (installationId === undefined) {
      enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
      return
    }

    try {
      const accountLink = await getAccountLinkByAccountId(installationId, companyId)

      if (!accountLink) {
        enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
        return
      }

      await removeAccountFromAccess(Number(installationId), accountLink.id, reason)
    } catch (error) {
      console.error('Failed to remove company access', error)
      enqueueErrorStack(t('installation.details.info.remove-access.error-message'))
    }
  }

  const onEditAccessStartAt = async (company: CompanyWithAccountLinkData): Promise<void> => {
    if (installationId === undefined) {
      enqueueErrorStack(t('installation.details.info.edit-access-start-at.error-message'))
      return
    }

    try {
      const accountLink = await getAccountLinkByAccountId(installationId, company.companyId)

      if (!accountLink) {
        enqueueErrorStack(t('installation.details.info.edit-access-start-at.error-message'))
        return
      }

      openEditAccessStartAtModal({
        startAt: new Date(company.startAt ?? ''),
        installationId: Number(installationId),
        accountLinkId: accountLink.id,
        refetchData: refetchAttachedCompanies,
      })
    } catch (error) {
      console.error('Failed to load account link for editing start date', error)
      enqueueErrorStack(t('installation.details.info.edit-access-start-at.error-message'))
    }
  }

  if (companies.length === 0) {
    return (
      <Box sx={{ m: 2 }}>
        <Typography variant={'body2'}>{t('installation.details.info.no-companies-with-access')}</Typography>
      </Box>
    )
  }

  const columns: Header<CompanyTableRow>[] = [
    {
      value: 'companyName',
      label: t('common.name'),
      isSortable: false,
      valueRender: (value, _, company) => (company?.companyId ? <CompanyName value={value ?? '-'} companyId={company?.companyId} /> : value),
    },
    {
      value: 'companyId',
      label: t('common.id'),
      isSortable: false,
    },
    {
      value: 'companyAddressId',
      label: t('common.address'),
      valueRender: (_, __, company) => <CompanyAddress company={company} />,
      isSortable: false,
    },
    {
      value: 'locationEmail',
      label: t('common.email'),
      valueRender: value => value || '-',
    },
    {
      value: 'locationPhone',
      label: t('common.phone'),
      valueRender: value => value || '-',
      isSortable: false,
    },
    {
      value: 'hasReadAccess',
      label: t('installation.access-and-invitations.table.header.columns.can-read'),
      valueRender: value => <RenderAccessIcon hasAccess={value} />,
      isSortable: false,
    },
    {
      value: 'startAt',
      label: t('installation.details.info.owner-start-access-at'),
      valueRender: (value, _, company) =>
        company ? (
          <StartAtComponent
            value={value}
            company={company}
            isInstallationDecommissioned={isInstallationDecommissioned}
            onEditAccessStartAt={onEditAccessStartAt}
          />
        ) : (
          '-'
        ),
      isSortable: false,
    },
    {
      value: 'hasWriteAccess',
      label: t('installation.access-and-invitations.table.header.columns.can-write'),
      valueRender: value => <RenderAccessIcon hasAccess={value} />,
      isSortable: false,
    },
    {
      value: 'remove',
      label: t('installation.details.info.remove-access.button-title'),
      valueRender: value => value,
      isSortable: false,
    },
  ]

  const tableData: CompanyTableRow[] = companies.map(company => ({
    ...company,
    remove: isInstallationDecommissioned ? null : (
      <Box sx={tableCellButtonSx}>
        <Button
          variant="text"
          onClick={() =>
            openReasonDialog({
              title: t('installation.details.info.remove-access.dialog-title'),
              description: t('installation.details.info.remove-access.dialog-text', {
                companyName: company.companyName ?? '',
              }),
              onSubmit: values => onRemoveUserFromCompany(company.companyId, values.reason),
            })
          }
        >
          <Typography variant="overline">{t('installation.details.info.remove-access.button-title')}</Typography>
        </Button>
      </Box>
    ),
  }))

  return (
    <Box sx={getCompactTableSx()}>
      <TableComplete
        header={columns}
        data={tableData}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
        disablePagination
        allowOtherCellColors
      />
    </Box>
  )
}

export default CompaniesWithAccess
